/**
 * Kitchen-wide measurement display mode (Settings > My Kitchen).
 *
 * Standard = weight → volume by density with cook shorthand; Technical =
 * exactly as the menus team authored it. Set once by a chef-scoped page
 * (orders/kitchen, shopping-list) from the active kitchen's organization;
 * read by every surface that formats a recipe amount — the deliverable
 * detail panel, recipe sections, and the printed recipe pack (portaled
 * from the same page, so context reaches it).
 *
 * Never blocks a recipe render: while loading, on error, or with no
 * provider mounted, the mode reads as the product default, `standard`.
 */
import { createContext, type ReactNode, useContext, useMemo } from 'react'

import { toMeasurementDisplay, useKitchenMeasurementPreferenceQuery } from '@/api/kitchen-settings'
import type { MeasurementDisplay } from '@/lib/chefs/recipe-quantities'

export interface MeasurementPreference {
  mode: MeasurementDisplay
  isLoading: boolean
}

const DEFAULT_PREFERENCE: MeasurementPreference = { mode: 'standard', isLoading: false }

const MeasurementPreferenceContext = createContext<MeasurementPreference>(DEFAULT_PREFERENCE)

interface ProviderProps {
  /** The active kitchen's organization id (`KitchenLocation.organizationId`). */
  kitchenOrganizationId: string | null
  children: ReactNode
}

export function MeasurementPreferenceProvider({ kitchenOrganizationId, children }: ProviderProps) {
  const query = useKitchenMeasurementPreferenceQuery(kitchenOrganizationId)
  const value = useMemo<MeasurementPreference>(
    () => ({
      mode: toMeasurementDisplay(query.data?.measurementPreference.displayMode),
      isLoading: Boolean(kitchenOrganizationId) && query.isPending,
    }),
    [kitchenOrganizationId, query.data, query.isPending],
  )
  return <MeasurementPreferenceContext.Provider value={value}>{children}</MeasurementPreferenceContext.Provider>
}

export function useMeasurementPreference(): MeasurementPreference {
  return useContext(MeasurementPreferenceContext)
}
